import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import { FocusShell } from '@/components/layout';
import { Alert, Button, Spinner } from '@/components/ui';
import { useT } from '@/i18n';

/**
 * Where a parent who answered as a guest lands straight after signing up or logging in: the
 * check-in they finished is sent off, and they go to its result. It needs a full account, so the
 * route guard is what asks a guest to sign up (`?r=1`: they came from the review step).
 */
export const FinishCheckin = () => {
  const t = useT();
  const navigate = useNavigate();
  const { assessmentId = '' } = useParams();
  const [params] = useSearchParams();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const sent = useRef(-1);

  useEffect(() => {
    // Once per attempt: a second submit of the same check-in is an error, not a no-op.
    if (sent.current === attempt) return;
    sent.current = attempt;
    setFailed(false);
    void (async () => {
      try {
        if (params.get('r') === '1') await api.assessments.review(assessmentId);
        await api.assessments.submit(assessmentId);
        navigate(`/sessions/${assessmentId}`, { replace: true });
      } catch (err) {
        if (!(err instanceof ApiError)) throw err;
        // Already sent (a refresh, or two tabs): the result is there.
        if (err.code === 'assessment_already_submitted' || err.status === 409) {
          navigate(`/sessions/${assessmentId}`, { replace: true });
          return;
        }
        setFailed(true);
      }
    })();
  }, [assessmentId, attempt, navigate, params]);

  return (
    <FocusShell>
      {failed ? (
        <div className="grid gap-4">
          <Alert tone="warn">{t('guest.finishError')}</Alert>
          <Button onClick={() => setAttempt((n) => n + 1)}>{t('common.retry')}</Button>
          <Button variant="secondary" onClick={() => navigate(`/assessments/${assessmentId}/review`)}>
            {t('guest.backToAnswers')}
          </Button>
        </div>
      ) : (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 text-center text-lagoon-600" role="status">
          <Spinner />
          <p className="text-body text-ink">{t('guest.finishing')}</p>
        </div>
      )}
    </FocusShell>
  );
};
